// BulkAcceptModal.js — Bulk accept alarms confirmation modal (PRD-49)

import React, { useState, useEffect } from 'react';
import { bulkDisposition } from '../services/driftService';

const DURATION_OPTIONS = [
    { label: '1 month', days: 30 },
    { label: '3 months', days: 90 },
    { label: '6 months', days: 180 },
    { label: '12 months', days: 365 },
];

function BulkAcceptModal({ isOpen, onClose, alarmIds, onSuccess, defaultScope = 'host', smartScopeReason = null }) {
    const [reason, setReason] = useState('');
    const [expiresInDays, setExpiresInDays] = useState(90);
    const [scope, setScope] = useState(defaultScope);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Re-sync scope when defaultScope changes (e.g., selection changes while modal mounted)
    useEffect(() => { setScope(defaultScope); }, [defaultScope]);

    if (!isOpen || !alarmIds || alarmIds.length === 0) return null;

    const count = alarmIds.length;

    const handleSubmit = async () => {
        if (reason.trim().length < 10) {
            setError('Reason must be at least 10 characters.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await bulkDisposition(alarmIds, 'accept', {
                expiresInDays,
                scope,
                reason: reason.trim(),
            });
            onSuccess && onSuccess(result);
            onClose();
        } catch (err) {
            setError(err.message || 'Bulk accept failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="alarm-modal-backdrop" onClick={onClose}>
            <div className="alarm-modal" onClick={e => e.stopPropagation()}>
                <div className="alarm-modal__header">
                    <h3>Accept {count} Alarm{count !== 1 ? 's' : ''}</h3>
                    <button className="alarm-modal__close" onClick={onClose}>×</button>
                </div>
                <div className="alarm-modal__body">
                    <p className="alarm-modal__desc">
                        Accepting <strong>{count} alarm{count !== 1 ? 's' : ''}</strong> marks them as known, acceptable risks.
                        Findings stay in your risk score, but alarms will not fire again until suppressions expire.
                    </p>

                    <div className="alarm-modal__field">
                        <label>Duration</label>
                        <div className="alarm-modal__pills">
                            {DURATION_OPTIONS.map(opt => (
                                <button
                                    key={opt.days}
                                    className={`alarm-modal__pill ${expiresInDays === opt.days ? 'active' : ''}`}
                                    onClick={() => setExpiresInDays(opt.days)}
                                    type="button"
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="alarm-modal__field">
                        <label>Scope</label>
                        <div className="alarm-modal__pills">
                            <button
                                className={`alarm-modal__pill ${scope === 'host' ? 'active' : ''}`}
                                onClick={() => setScope('host')}
                                type="button"
                            >
                                This detection only
                            </button>
                            <button
                                className={`alarm-modal__pill ${scope === 'asset' ? 'active' : ''}`}
                                onClick={() => setScope('asset')}
                                type="button"
                            >
                                All occurrences on this asset
                            </button>
                        </div>
                        {smartScopeReason && (
                            <p className="alarm-modal__scope-hint alarm-modal__scope-hint--smart">
                                {smartScopeReason} Toggle off if you want per-detection.
                            </p>
                        )}
                        {!smartScopeReason && (
                            <p className="alarm-modal__scope-hint">
                                {scope === 'host'
                                    ? 'Suppresses only the exact matched location for each selected alarm.'
                                    : 'Future findings of the same type on this asset will be auto-accepted until suppressions expire.'}
                            </p>
                        )}
                    </div>

                    <div className="alarm-modal__field">
                        <label>Reason <span className="alarm-modal__hint">(min 10 chars)</span></label>
                        <textarea
                            className="alarm-modal__textarea"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g., Initial baseline review — accepting all known medium risks for Q2"
                            rows={3}
                        />
                    </div>

                    {error && <div className="alarm-modal__error">{error}</div>}
                </div>
                <div className="alarm-modal__footer">
                    <button className="alarm-modal__btn alarm-modal__btn--cancel" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className="alarm-modal__btn alarm-modal__btn--accept"
                        onClick={handleSubmit}
                        disabled={loading || reason.trim().length < 10}
                    >
                        {loading ? `Accepting ${count}...` : `Accept ${count} Alarm${count !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BulkAcceptModal;
